import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Archive,
  BarChart3,
  BookOpen,
  ArrowLeftRight,
  History,
  RefreshCw,
  Save,
  ShieldCheck,
  UploadCloud
} from 'lucide-react';
import apiService, { apiActor } from '../../services/apiService';
import { getActiveForecastUser } from './forecastUsers';
import NumericInput from './NumericInput';

const CYCLE_CODE = 'FY2027_BUDGET';
const FORECAST_YEARS = [2027, 2028, 2029, 2030, 2031];

const BASE_VARIABLES = {
  sofr: 4.85,
  gdp: 2.1,
  inflation: 2.6,
  unemployment: 4.1,
  depositBeta: 38,
  creditLoss: 0.42,
  loanDemand: 1,
  prepayment: 8.5,
  depositRunoff: 3.8,
  expenseInflation: 3.1,
  wageInflation: 3.8
};

const VARIABLE_DEFS = [
  { key: 'sofr', label: 'SOFR %', step: 0.05, min: 0, max: 9, digits: 2 },
  { key: 'gdp', label: 'GDP %', step: 0.1, min: -4, max: 6, digits: 1 },
  { key: 'inflation', label: 'Inflation %', step: 0.1, min: 0, max: 9, digits: 1 },
  { key: 'unemployment', label: 'Unemployment %', step: 0.1, min: 2, max: 12, digits: 1 },
  { key: 'depositBeta', label: 'Deposit beta', step: 1, min: 5, max: 90, digits: 0 },
  { key: 'creditLoss', label: 'Credit loss %', step: 0.02, min: 0, max: 3, digits: 2 },
  { key: 'loanDemand', label: 'Loan demand index', step: 0.01, min: 0.5, max: 1.5, digits: 2 },
  { key: 'prepayment', label: 'Prepayment %', step: 0.1, min: 0, max: 20, digits: 1 },
  { key: 'depositRunoff', label: 'Deposit runoff %', step: 0.1, min: 0, max: 20, digits: 1 },
  { key: 'expenseInflation', label: 'Expense inflation %', step: 0.1, min: 0, max: 8, digits: 1 },
  { key: 'wageInflation', label: 'Wage inflation %', step: 0.1, min: 0, max: 8, digits: 1 }
];

const STATUS_TONE = {
  draft: 'amber',
  published: 'moss',
  archived: 'neutral'
};

const humanizeKey = (key) =>
  String(key || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const formatNumber = (value, digits = 1) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '-';
  return Number(value).toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits
  });
};

const hasMacroManagePermission = (roleContext) => {
  const roles = (roleContext?.roles || []).map((role) => String(role).toLowerCase());
  const permissions = (roleContext?.permissions || []).map((permission) => String(permission).toLowerCase());
  return roles.includes('central_fpa') || permissions.includes('macro:manage');
};

const normalizeScenarioList = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  return Array.isArray(payload.scenarios) ? payload.scenarios : [];
};

const buildVariableDefs = (scenarios) => {
  const known = new Map(VARIABLE_DEFS.map((def) => [def.key, def]));
  scenarios.forEach((scenario) => {
    Object.keys(scenario?.variables || {}).forEach((key) => {
      if (!known.has(key)) {
        known.set(key, {
          key,
          label: humanizeKey(key),
          step: 0.1,
          min: undefined,
          max: undefined,
          digits: 2
        });
      }
    });
  });
  return Array.from(known.values());
};

const resolveYearValue = (rawValue, key, year) => {
  const defaultValue = BASE_VARIABLES[key] ?? 0;
  if (rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)) {
    const yearValue = rawValue[String(year)] ?? rawValue[year];
    return Number.isFinite(Number(yearValue)) ? Number(yearValue) : defaultValue;
  }
  return Number.isFinite(Number(rawValue)) ? Number(rawValue) : defaultValue;
};

const normalizeVariablesToPaths = (variables = {}, variableDefs = VARIABLE_DEFS) => {
  const output = {};
  variableDefs.forEach((def) => {
    output[def.key] = FORECAST_YEARS.reduce((path, year) => ({
      ...path,
      [String(year)]: resolveYearValue(variables[def.key], def.key, year)
    }), {});
  });
  return output;
};

const pathToSaveShape = (paths = {}, variableDefs = VARIABLE_DEFS) =>
  variableDefs.reduce((payload, def) => ({
    ...payload,
    [def.key]: FORECAST_YEARS.reduce((path, year) => ({
      ...path,
      [String(year)]: Number(paths[def.key]?.[String(year)] ?? BASE_VARIABLES[def.key] ?? 0)
    }), {})
  }), {});

const isCustomPath = (path = {}) => {
  const values = FORECAST_YEARS.map((year) => path[String(year)]);
  return values.some((value) => Number(value) !== Number(values[0]));
};

const scenarioLabel = (scenario) =>
  scenario ? `${scenario.label || scenario.key} v${scenario.version || 1}` : '-';

const scenarioRowId = (scenario) =>
  `${scenario?.key || ''}::${scenario?.version || 1}::${scenario?.status || 'draft'}`;

const parseScenarioRowId = (rowId, scenarios = []) =>
  scenarios.find((scenario) => scenarioRowId(scenario) === rowId) || null;

const formatDeltaMoney = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return '-';
  const sign = number > 0 ? '+' : '';
  return `${sign}${number.toLocaleString('en-US', { maximumFractionDigits: 1, minimumFractionDigits: 1 })}`;
};

const metricDeltaClass = (value, invert = false) => {
  const number = Number(value) || 0;
  const adjusted = invert ? number * -1 : number;
  if (adjusted > 0) return 'pos';
  if (adjusted < 0) return 'neg';
  return '';
};

const buildCumulativeWaterfall = (rows = []) => {
  let cumulative = 0;
  return rows.map((row) => {
    const start = cumulative;
    const delta = Number(row.netIncomeDelta) || 0;
    cumulative += delta;
    return {
      year: row.year,
      start,
      delta,
      end: cumulative
    };
  });
};

const ForecastMacroScenarios = () => {
  const [activeUser] = useState(getActiveForecastUser);
  const [roleContext, setRoleContext] = useState(null);
  const [roleReady, setRoleReady] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [diffAId, setDiffAId] = useState('');
  const [diffBId, setDiffBId] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadNotice, setLoadNotice] = useState('');
  const [error, setError] = useState('');
  const [writeError, setWriteError] = useState('');
  const [saveState, setSaveState] = useState('');
  const [editorLabel, setEditorLabel] = useState('');
  const [editorNotes, setEditorNotes] = useState('');
  const [editorVariables, setEditorVariables] = useState({});
  const [draftFromPublished, setDraftFromPublished] = useState(false);
  const [noteDialog, setNoteDialog] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [noteError, setNoteError] = useState('');
  const [rebuildAnchorsOnPublish, setRebuildAnchorsOnPublish] = useState(false);
  const [impactTab, setImpactTab] = useState(() => (
    new URLSearchParams(window.location.search).get('tab') === 'compare' ? 'compare' : 'variable'
  ));
  const [planningEntities, setPlanningEntities] = useState([]);
  const [planningLoading, setPlanningLoading] = useState(false);
  const [compareLevel, setCompareLevel] = useState('entity');
  const [compareEntity, setCompareEntity] = useState('');
  const [compareAId, setCompareAId] = useState('');
  const [compareBId, setCompareBId] = useState('');
  const [compareBusy, setCompareBusy] = useState(false);
  const [compareError, setCompareError] = useState('');
  const [compareResult, setCompareResult] = useState(null);
  // M4: explicit Central FP&A re-anchor of the cycle's driver-view anchors.
  const [reanchorBusy, setReanchorBusy] = useState(false);
  const [reanchorNote, setReanchorNote] = useState('');
  const [reanchorResult, setReanchorResult] = useState(null);
  const [reanchorError, setReanchorError] = useState('');

  const loadScenarios = useCallback(async () => {
    setLoading(true);
    setError('');
    setLoadNotice('');
    try {
      const payload = await apiService.fetchMacroScenarios(CYCLE_CODE);
      const rows = normalizeScenarioList(payload);
      setScenarios(rows);
      if (rows.length === 0) {
        setLoadNotice('No macro scenario rows returned for this cycle yet.');
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        setScenarios([]);
        setLoadNotice('Macro scenario store is not available yet; showing an empty scenario book.');
      } else if (err?.response?.status === 403) {
        setScenarios([]);
        setError('You can open the console, but the server denied scenario list access.');
      } else {
        setScenarios([]);
        setError(err?.response?.data?.error || err.message || 'Failed to load macro scenarios.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setRoleReady(false);
    apiService.fetchForecastRoleContext(apiActor(activeUser), CYCLE_CODE)
      .then((context) => {
        if (!cancelled) setRoleContext(context);
      })
      .catch(() => {
        if (!cancelled) setRoleContext(null);
      })
      .finally(() => {
        if (!cancelled) setRoleReady(true);
      });
    return () => { cancelled = true; };
  }, [activeUser]);

  useEffect(() => {
    loadScenarios();
  }, [loadScenarios]);

  useEffect(() => {
    let cancelled = false;
    setPlanningLoading(true);
    apiService.fetchForecastPlanningEntities()
      .then((rows) => {
        if (!cancelled) {
          const normalized = Array.isArray(rows) ? rows : [];
          setPlanningEntities(normalized);
          if (!compareEntity && normalized[0]?.planningEntity) {
            setCompareEntity(normalized[0].planningEntity);
          }
        }
      })
      .catch(() => {
        if (!cancelled) setPlanningEntities([]);
      })
      .finally(() => {
        if (!cancelled) setPlanningLoading(false);
      });
    return () => { cancelled = true; };
  }, [compareEntity]);

  const variableDefs = useMemo(() => buildVariableDefs(scenarios), [scenarios]);
  const canManage = hasMacroManagePermission(roleContext);
  const publishedScenarios = useMemo(
    () => scenarios.filter((scenario) => scenario.status === 'published'),
    [scenarios]
  );

  useEffect(() => {
    if (scenarios.length === 0) {
      setSelectedId('');
      setDiffAId('');
      setDiffBId('');
      return;
    }
    if (!selectedId || !scenarios.some((scenario) => scenarioRowId(scenario) === selectedId)) {
      setSelectedId(scenarioRowId(scenarios[0]));
    }
    if (!diffAId || !scenarios.some((scenario) => scenarioRowId(scenario) === diffAId)) {
      setDiffAId(scenarioRowId(scenarios[0]));
    }
    if (!diffBId || !scenarios.some((scenario) => scenarioRowId(scenario) === diffBId)) {
      setDiffBId(scenarioRowId(scenarios[1] || scenarios[0]));
    }
    if (!compareAId || !publishedScenarios.some((scenario) => scenarioRowId(scenario) === compareAId)) {
      setCompareAId(scenarioRowId(publishedScenarios[0] || scenarios[0]));
    }
    if (!compareBId || !publishedScenarios.some((scenario) => scenarioRowId(scenario) === compareBId)) {
      setCompareBId(scenarioRowId(publishedScenarios[1] || publishedScenarios[0] || scenarios[0]));
    }
  }, [compareAId, compareBId, diffAId, diffBId, publishedScenarios, scenarios, selectedId]);

  const selectedScenario = useMemo(
    () => scenarios.find((scenario) => scenarioRowId(scenario) === selectedId) || null,
    [scenarios, selectedId]
  );

  useEffect(() => {
    if (!selectedScenario) {
      setEditorLabel('');
      setEditorNotes('');
      setEditorVariables(normalizeVariablesToPaths({}, variableDefs));
      setDraftFromPublished(false);
      return;
    }
    setEditorLabel(selectedScenario.label || selectedScenario.key || '');
    setEditorNotes(selectedScenario.notes || '');
    setEditorVariables(normalizeVariablesToPaths(selectedScenario.variables || {}, variableDefs));
    setDraftFromPublished(false);
    setSaveState('');
    setWriteError('');
  }, [selectedScenario, variableDefs]);

  const diffScenarioA = useMemo(
    () => scenarios.find((scenario) => scenarioRowId(scenario) === diffAId) || scenarios[0] || null,
    [diffAId, scenarios]
  );
  const diffScenarioB = useMemo(
    () => scenarios.find((scenario) => scenarioRowId(scenario) === diffBId) || scenarios[1] || scenarios[0] || null,
    [diffBId, scenarios]
  );
  const compareScenarioA = useMemo(
    () => parseScenarioRowId(compareAId, scenarios),
    [compareAId, scenarios]
  );
  const compareScenarioB = useMemo(
    () => parseScenarioRowId(compareBId, scenarios),
    [compareBId, scenarios]
  );

  const diffRows = useMemo(() => {
    if (!diffScenarioA || !diffScenarioB) return [];
    const pathsA = normalizeVariablesToPaths(diffScenarioA.variables || {}, variableDefs);
    const pathsB = normalizeVariablesToPaths(diffScenarioB.variables || {}, variableDefs);
    return variableDefs.flatMap((def) => FORECAST_YEARS.map((year) => {
      const a = pathsA[def.key]?.[String(year)];
      const b = pathsB[def.key]?.[String(year)];
      return {
        key: `${def.key}-${year}`,
        variable: def.label,
        year,
        a,
        b,
        delta: Number(b) - Number(a),
        digits: def.digits ?? 1
      };
    }));
  }, [diffScenarioA, diffScenarioB, variableDefs]);

  const eventRows = useMemo(() => {
    if (!selectedScenario) return [];
    const explicitEvents = selectedScenario.events || selectedScenario.eventLog || selectedScenario.history;
    if (Array.isArray(explicitEvents) && explicitEvents.length > 0) {
      return explicitEvents.map((event, index) => ({
        id: event.id || `${selectedScenario.key}-event-${index}`,
        event: event.event || event.status || event.action || 'scenario update',
        actor: event.actor || event.updatedBy || selectedScenario.updatedBy || '-',
        note: event.note || event.notes || '-',
        at: event.createdAt || event.created_at || event.updatedAt || selectedScenario.updatedAt
      }));
    }
    return scenarios
      .filter((scenario) => scenario.key === selectedScenario.key)
      .sort((a, b) => Number(b.version || 0) - Number(a.version || 0))
      .map((scenario) => ({
        id: `${scenario.key}-${scenario.version || 1}-${scenario.status || 'draft'}`,
        event: `${scenario.status || 'draft'} v${scenario.version || 1}`,
        actor: scenario.updatedBy || '-',
        note: scenario.notes || 'List data fallback',
        at: scenario.updatedAt
      }));
  }, [scenarios, selectedScenario]);

  const compareDiffRows = useMemo(() => {
    if (!compareScenarioA || !compareScenarioB) return [];
    const pathsA = normalizeVariablesToPaths(compareScenarioA.variables || {}, variableDefs);
    const pathsB = normalizeVariablesToPaths(compareScenarioB.variables || {}, variableDefs);
    return variableDefs.flatMap((def) => FORECAST_YEARS.map((year) => {
      const a = pathsA[def.key]?.[String(year)];
      const b = pathsB[def.key]?.[String(year)];
      return {
        key: `${def.key}-${year}`,
        variable: def.label,
        year,
        a,
        b,
        delta: Number(b) - Number(a),
        digits: def.digits ?? 1
      };
    })).filter((row) => Math.abs(row.delta) > 0.0000001);
  }, [compareScenarioA, compareScenarioB, variableDefs]);

  const waterfallRows = useMemo(
    () => buildCumulativeWaterfall(compareResult?.rows || []),
    [compareResult]
  );
  const waterfallMax = useMemo(
    () => Math.max(1, ...waterfallRows.flatMap((row) => [Math.abs(row.start), Math.abs(row.end), Math.abs(row.delta)])),
    [waterfallRows]
  );

  const editorReadOnlyReason = useMemo(() => {
    if (!selectedScenario) return 'Select a scenario to edit.';
    if (!canManage) return 'Read-only: macro:manage required.';
    if (selectedScenario.status === 'archived') return 'Archived scenarios are read-only.';
    if (selectedScenario.status === 'published' && !draftFromPublished) {
      return 'Published rows are immutable; create a draft version to edit.';
    }
    return '';
  }, [canManage, draftFromPublished, selectedScenario]);

  const editorDisabled = Boolean(editorReadOnlyReason);

  const setAllYears = (def, value) => {
    setEditorVariables((previous) => ({
      ...previous,
      [def.key]: FORECAST_YEARS.reduce((path, year) => ({
        ...path,
        [String(year)]: value
      }), {})
    }));
    setSaveState('');
  };

  const setYearValue = (def, year, value) => {
    setEditorVariables((previous) => ({
      ...previous,
      [def.key]: {
        ...(previous[def.key] || {}),
        [String(year)]: value
      }
    }));
    setSaveState('');
  };

  const handleSave = async () => {
    if (!selectedScenario || editorDisabled) return;
    setWriteError('');
    setSaveState('Saving...');
    try {
      await apiService.saveMacroScenario(selectedScenario.key, {
        cycleCode: CYCLE_CODE,
        actor: apiActor(activeUser),
        label: editorLabel,
        variables: pathToSaveShape(editorVariables, variableDefs),
        notes: editorNotes
      });
      setSaveState('Saved draft');
      await loadScenarios();
    } catch (err) {
      const permission = err?.response?.data?.requiredPermission;
      setSaveState('');
      setWriteError(permission
        ? `Server denied write; required permission: ${permission}.`
        : err?.response?.data?.error || err.message || 'Failed to save macro scenario.');
    }
  };

  const openNoteDialog = (action, scenario = selectedScenario) => {
    if (!scenario || !canManage) return;
    setNoteDialog({ action, scenario });
    setNoteDraft('');
    setNoteError('');
    setWriteError('');
    setRebuildAnchorsOnPublish(false);
  };

  const closeNoteDialog = () => {
    setNoteDialog(null);
    setNoteDraft('');
    setNoteError('');
    setRebuildAnchorsOnPublish(false);
  };

  const handleActionWithNote = async () => {
    if (!noteDialog) return;
    const note = noteDraft.trim();
    if (!note) {
      setNoteError(`${noteDialog.action === 'publish' ? 'Publish' : 'Archive'} note is required.`);
      return;
    }
    setNoteError('');
    setWriteError('');
    try {
      const payload = { cycleCode: CYCLE_CODE, actor: apiActor(activeUser), note };
      if (noteDialog.action === 'publish') {
        await apiService.publishMacroScenario(noteDialog.scenario.key, {
          ...payload,
          rebuildAnchors: rebuildAnchorsOnPublish
        });
      } else {
        await apiService.archiveMacroScenario(noteDialog.scenario.key, payload);
      }
      closeNoteDialog();
      await loadScenarios();
    } catch (err) {
      const permission = err?.response?.data?.requiredPermission;
      setWriteError(permission
        ? `Server denied ${noteDialog.action}; required permission: ${permission}.`
        : err?.response?.data?.error || err.message || `Failed to ${noteDialog.action} scenario.`);
    }
  };

  const runComparison = async () => {
    if (!compareScenarioA || !compareScenarioB) {
      setCompareError('Select two published scenario versions.');
      return;
    }
    if (compareLevel === 'entity' && !compareEntity) {
      setCompareError('Select a unit for entity-level comparison.');
      return;
    }
    setCompareBusy(true);
    setCompareError('');
    try {
      const payload = {
        cycleCode: CYCLE_CODE,
        actor: apiActor(activeUser),
        level: compareLevel,
        entity: compareLevel === 'entity' ? compareEntity : undefined,
        scenarioA: { key: compareScenarioA.key, version: compareScenarioA.version },
        scenarioB: { key: compareScenarioB.key, version: compareScenarioB.version },
        inputSource: 'latestSubmissionOrSeed',
        holdInputterLeversConstant: true,
        holdShocksConstant: false
      };
      const result = await apiService.compareMacroScenarios(payload);
      setCompareResult(result);
    } catch (err) {
      setCompareResult(null);
      setCompareError(err?.response?.data?.error || err.message || 'Failed to compare macro scenarios.');
    } finally {
      setCompareBusy(false);
    }
  };

  const publishedBase = useMemo(
    () => publishedScenarios.find((scenario) => String(scenario.key).toLowerCase() === 'base') || null,
    [publishedScenarios]
  );

  const runReanchor = async () => {
    if (!canManage || !publishedBase) return;
    const note = reanchorNote.trim();
    if (!note) {
      setReanchorError('A re-anchor note is required.');
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm(
      `Re-anchor every unit's driver view to Base v${publishedBase.version}? `
      + 'This supersedes the current active anchors (immutably pinned).'
    )) {
      return;
    }
    setReanchorBusy(true);
    setReanchorError('');
    setReanchorResult(null);
    try {
      const result = await apiService.rebuildDriverViewAnchors({
        cycleCode: CYCLE_CODE,
        actor: apiActor(activeUser),
        scenarioKey: 'Base',
        scenarioVersion: publishedBase.version,
        scope: 'all',
        note
      });
      setReanchorResult(result);
      setReanchorNote('');
      await loadScenarios();
    } catch (err) {
      const permission = err?.response?.data?.requiredPermission;
      setReanchorError(permission
        ? `Server denied re-anchor; required permission: ${permission}.`
        : err?.response?.data?.error || err.message || 'Failed to rebuild driver-view anchors.');
    } finally {
      setReanchorBusy(false);
    }
  };

  return (
    <div className="forecast-module forecast-macro">
      <section className="forecast-command">
        <div>
          <div className="forecast-kicker">
            <BarChart3 size={14} />
            Macro Environment / Scenario Management
          </div>
          <h1>Macro scenario console</h1>
          <p>
            Maintain central FP&A macro scenario versions, compare variable paths, and stage the
            deterministic impact preview without duplicating the forecast model math.
          </p>
        </div>

        <div className="macro-command-stack">
          <span className={`forecast-health-chip ${canManage ? 'moss' : 'neutral'}`}>
            {roleReady ? (canManage ? 'macro:manage enabled' : 'read-only console') : 'checking permissions'}
          </span>
          <span className="forecast-health-chip neutral">
            Actor: {apiActor(activeUser) || '-'}
          </span>
          <button type="button" className="forecast-secondary-button compact" onClick={loadScenarios}>
            <RefreshCw size={13} /> Refresh scenarios
          </button>
        </div>
      </section>

      {(loadNotice || error || writeError || saveState) && (
        <section className={`forecast-panel macro-notice${error || writeError ? ' error' : ''}`}>
          {loadNotice && <p>{loadNotice}</p>}
          {error && <p>{error}</p>}
          {writeError && <p>{writeError}</p>}
          {saveState && <p>{saveState}</p>}
        </section>
      )}

      <div className="macro-console-grid">
        <section className="forecast-panel">
          <div className="forecast-panel-head">
            <div>
              <span className="forecast-section-code">MX_01 Scenario book</span>
              <h2>Scenarios and versions</h2>
            </div>
            <BookOpen size={18} />
          </div>

          {loading ? (
            <p className="forecast-home-loading">Loading macro scenarios...</p>
          ) : scenarios.length === 0 ? (
            <p className="forecast-submit-note">No scenarios available for {CYCLE_CODE}.</p>
          ) : (
            <div className="macro-scenario-list" aria-label="Macro scenario list">
              {scenarios.map((scenario) => {
                const rowId = scenarioRowId(scenario);
                const isActive = selectedId === rowId;
                const status = scenario.status || 'draft';
                return (
                  <button
                    type="button"
                    key={`${scenario.key}-${scenario.version || 1}-${status}`}
                    className={`macro-scenario-card ${isActive ? 'active' : ''}`}
                    aria-pressed={isActive}
                    onClick={() => setSelectedId(rowId)}
                  >
                    <span>
                      <strong>{scenario.label || scenario.key}</strong>
                      <small>{scenario.key}</small>
                    </span>
                    <span className="macro-scenario-meta">
                      <span className={`forecast-health-chip ${STATUS_TONE[status] || 'neutral'}`}>{status}</span>
                      <span className="forecast-health-chip neutral">v{scenario.version || 1}</span>
                    </span>
                    <small>
                      {scenario.updatedBy || '-'} / {formatDateTime(scenario.updatedAt)}
                    </small>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section className="forecast-panel macro-editor-panel">
          <div className="forecast-panel-head">
            <div>
              <span className="forecast-section-code">MX_02 Editor</span>
              <h2>Per-year variable paths</h2>
            </div>
            <ShieldCheck size={18} />
          </div>

          {selectedScenario ? (
            <>
              <div className="macro-editor-fields">
                <label>
                  <span>Scenario label</span>
                  <input
                    className="macro-text-input"
                    value={editorLabel}
                    disabled={editorDisabled}
                    onChange={(event) => setEditorLabel(event.target.value)}
                  />
                </label>
                <label>
                  <span>Draft notes</span>
                  <textarea
                    className="macro-textarea"
                    value={editorNotes}
                    disabled={editorDisabled}
                    onChange={(event) => setEditorNotes(event.target.value)}
                  />
                </label>
              </div>

              <div className="forecast-submit-chips">
                <span className={`forecast-health-chip ${STATUS_TONE[selectedScenario.status] || 'neutral'}`}>
                  {selectedScenario.status || 'draft'} v{selectedScenario.version || 1}
                </span>
                {draftFromPublished && (
                  <span className="forecast-health-chip amber">editing new draft version</span>
                )}
                {editorReadOnlyReason && (
                  <span className="forecast-health-chip neutral">{editorReadOnlyReason}</span>
                )}
              </div>

              {selectedScenario.status === 'published' && canManage && !draftFromPublished && (
                <button
                  type="button"
                  className="forecast-secondary-button compact"
                  onClick={() => setDraftFromPublished(true)}
                >
                  Create draft version
                </button>
              )}

              <div className="forecast-home-table-wrap macro-variable-table-wrap">
                <table className="forecast-home-table op3-year-table aligned macro-variable-table">
                  <thead>
                    <tr>
                      <th>Variable</th>
                      <th className="parent-col">All years</th>
                      {FORECAST_YEARS.map((year) => <th key={year}>FY{year}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {variableDefs.map((def) => {
                      const path = editorVariables[def.key] || {};
                      const custom = isCustomPath(path);
                      const values = FORECAST_YEARS.map((year) => path[String(year)]);
                      return (
                        <tr key={def.key}>
                          <td>
                            <strong>{def.label}</strong>
                            <small className="forecast-home-market"> {def.key}</small>
                          </td>
                          <td className="parent-col">
                            {custom ? (
                              <button
                                type="button"
                                className="lever-parent-locked"
                                disabled={editorDisabled}
                                title="Per-year custom values are set; click to broadcast FY2027 to all years"
                                onClick={() => setAllYears(def, values[0])}
                              >
                                custom lock
                              </button>
                            ) : (
                              <NumericInput
                                ariaLabel={`${def.label} all years`}
                                value={values[0]}
                                step={def.step}
                                min={def.min}
                                max={def.max}
                                disabled={editorDisabled}
                                title="Sets every fiscal year to this amount"
                                onCommit={(value) => setAllYears(def, value)}
                              />
                            )}
                          </td>
                          {FORECAST_YEARS.map((year) => (
                            <td key={year}>
                              <NumericInput
                                ariaLabel={`${def.label} FY${year}`}
                                value={path[String(year)]}
                                step={def.step}
                                min={def.min}
                                max={def.max}
                                disabled={editorDisabled}
                                onCommit={(value) => setYearValue(def, year, value)}
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="forecast-submit-note">
                  Enter one amount under All years to broadcast across every fiscal year. Editing a
                  single year locks the parent cell until FY2027 is broadcast back across the path.
                </p>
              </div>

              <div className="forecast-submit-actions">
                <button type="button" className="forecast-primary-button" disabled={editorDisabled} onClick={handleSave}>
                  <Save size={14} /> Save draft
                </button>
                <button
                  type="button"
                  className="forecast-secondary-button"
                  disabled={!canManage || selectedScenario.status === 'published' || selectedScenario.status === 'archived'}
                  onClick={() => openNoteDialog('publish')}
                >
                  <UploadCloud size={14} /> Publish
                </button>
                <button
                  type="button"
                  className="forecast-secondary-button"
                  disabled={!canManage || selectedScenario.status === 'archived'}
                  onClick={() => openNoteDialog('archive')}
                >
                  <Archive size={14} /> Archive
                </button>
              </div>
            </>
          ) : (
            <p className="forecast-submit-note">Select a scenario to edit its annual macro path.</p>
          )}
        </section>
      </div>

      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <div>
            <span className="forecast-section-code">MX_04 Driver-view anchors</span>
            <h2>Re-anchor the cycle to published Base</h2>
          </div>
          <ShieldCheck size={18} />
        </div>
        <p className="forecast-submit-note">
          Rebuilds every unit's top-down driver-view anchor from the published Base
          scenario (manual levers zeroed). Anchors are immutably pinned to the
          scenario version; the prior active anchors are superseded. Inputter plans
          are then measured against this anchor and must justify deviations beyond
          threshold (NI ±3%, loans/deposits ±2%).
        </p>
        <div className="macro-diff-controls">
          <label style={{ flex: 1 }}>
            Re-anchor note
            <input
              className="macro-text-input"
              value={reanchorNote}
              disabled={!canManage || !publishedBase || reanchorBusy}
              placeholder="Reason for re-anchoring (required)"
              onChange={(event) => setReanchorNote(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="forecast-primary-button"
            disabled={!canManage || !publishedBase || reanchorBusy}
            onClick={runReanchor}
          >
            <RefreshCw size={14} /> {reanchorBusy ? 'Re-anchoring…' : 'Rebuild anchors'}
          </button>
        </div>
        {!publishedBase && (
          <p className="forecast-submit-note">Publish a Base scenario before re-anchoring.</p>
        )}
        {reanchorError && <p className="macro-notice error">{reanchorError}</p>}
        {reanchorResult && (
          <p className="forecast-submit-note">
            Rebuilt {reanchorResult.anchorCount} anchor{reanchorResult.anchorCount === 1 ? '' : 's'}
            {' '}for Base v{reanchorResult.scenarioVersion} ({reanchorResult.modelVersion}).
          </p>
        )}
      </section>

      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <div>
            <span className="forecast-section-code">MX_03 Variable diff</span>
            <h2>Scenario A vs B variable deltas</h2>
          </div>
          <ArrowLeftRight size={18} />
        </div>

        <div className="macro-diff-controls">
          <label>
            Scenario A
            <select value={diffAId} onChange={(event) => setDiffAId(event.target.value)} disabled={scenarios.length === 0}>
              {scenarios.map((scenario) => (
                <option key={`a-${scenarioRowId(scenario)}`} value={scenarioRowId(scenario)}>{scenarioLabel(scenario)}</option>
              ))}
            </select>
          </label>
          <label>
            Scenario B
            <select value={diffBId} onChange={(event) => setDiffBId(event.target.value)} disabled={scenarios.length === 0}>
              {scenarios.map((scenario) => (
                <option key={`b-${scenarioRowId(scenario)}`} value={scenarioRowId(scenario)}>{scenarioLabel(scenario)}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="forecast-home-table-wrap">
          <table className="forecast-home-table macro-diff-table">
            <thead>
              <tr>
                <th>Variable</th>
                <th>Year</th>
                <th>{diffScenarioA?.label || 'A'}</th>
                <th>{diffScenarioB?.label || 'B'}</th>
                <th>Delta</th>
              </tr>
            </thead>
            <tbody>
              {diffRows.length === 0 ? (
                <tr><td colSpan="5">No scenarios available to compare.</td></tr>
              ) : diffRows.map((row) => (
                <tr key={row.key}>
                  <td>{row.variable}</td>
                  <td>FY{row.year}</td>
                  <td className="forecast-mono">{formatNumber(row.a, row.digits)}</td>
                  <td className="forecast-mono">{formatNumber(row.b, row.digits)}</td>
                  <td className={`forecast-mono ${row.delta > 0 ? 'pos' : row.delta < 0 ? 'neg' : ''}`}>
                    {row.delta > 0 ? '+' : ''}{formatNumber(row.delta, row.digits)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="forecast-panel macro-impact-preview" aria-label="Scenario impact preview">
        <div className="forecast-panel-head">
          <div>
            <span className="forecast-section-code">M3 Mount point</span>
            <h2>Macro isolation A/B</h2>
          </div>
          <BarChart3 size={18} />
        </div>
        <div id="macro-impact-preview-m3-mount" data-m3-mount="deterministic-preview">
          <div className="forecast-segmented">
            <button
              type="button"
              className={impactTab === 'compare' ? 'active' : ''}
              onClick={() => setImpactTab('compare')}
            >
              Comparison
            </button>
            <button
              type="button"
              className={impactTab === 'variable' ? 'active' : ''}
              onClick={() => setImpactTab('variable')}
            >
              Variable diff
            </button>
          </div>

          {impactTab === 'compare' ? (
            <div className="macro-compare-layout">
              <div className="macro-diff-controls">
                <label>
                  Scope
                  <select value={compareLevel} onChange={(event) => setCompareLevel(event.target.value)}>
                    <option value="entity">Unit</option>
                    <option value="company">Company</option>
                  </select>
                </label>
                {compareLevel === 'entity' && (
                  <label>
                    Unit
                    <select
                      value={compareEntity}
                      onChange={(event) => setCompareEntity(event.target.value)}
                      disabled={planningLoading || planningEntities.length === 0}
                    >
                      {planningEntities.map((entity) => (
                        <option key={entity.planningEntity} value={entity.planningEntity}>
                          {entity.planningEntity}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <label>
                  Scenario A
                  <select value={compareAId} onChange={(event) => setCompareAId(event.target.value)} disabled={publishedScenarios.length === 0}>
                    {publishedScenarios.map((scenario) => (
                      <option key={`compare-a-${scenarioRowId(scenario)}`} value={scenarioRowId(scenario)}>{scenarioLabel(scenario)}</option>
                    ))}
                  </select>
                </label>
                <label>
                  Scenario B
                  <select value={compareBId} onChange={(event) => setCompareBId(event.target.value)} disabled={publishedScenarios.length === 0}>
                    {publishedScenarios.map((scenario) => (
                      <option key={`compare-b-${scenarioRowId(scenario)}`} value={scenarioRowId(scenario)}>{scenarioLabel(scenario)}</option>
                    ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="forecast-primary-button compact"
                  onClick={runComparison}
                  disabled={compareBusy || publishedScenarios.length < 2}
                >
                  {compareBusy ? 'Comparing...' : 'Run comparison'}
                </button>
              </div>

              <p className="forecast-submit-note">
                Basis: macro isolation — inputter levers held constant, local shocks zeroed.
                {compareResult?.totalsComputedBy === 'server' ? ' Totals computed by server.' : ''}
              </p>
              {compareError && <p className="forecast-submit-note error-list">{compareError}</p>}

              <div className="macro-compare-grid">
                <div className="forecast-home-table-wrap">
                  <table className="forecast-home-table macro-diff-table">
                    <thead>
                      <tr>
                        <th>Year</th>
                        <th>NII</th>
                        <th>Fees</th>
                        <th>Expense</th>
                        <th>Provision</th>
                        <th>NI</th>
                        <th>Loans</th>
                        <th>Deposits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(compareResult?.rows || []).length === 0 ? (
                        <tr><td colSpan="8">Run comparison to load server-computed deltas.</td></tr>
                      ) : compareResult.rows.map((row) => (
                        <tr key={row.year}>
                          <td>FY{row.year}</td>
                          <td className={`forecast-mono ${metricDeltaClass(row.niiDelta)}`}>{formatDeltaMoney(row.niiDelta)}</td>
                          <td className={`forecast-mono ${metricDeltaClass(row.feesDelta)}`}>{formatDeltaMoney(row.feesDelta)}</td>
                          <td className={`forecast-mono ${metricDeltaClass(row.expenseDelta, true)}`}>{formatDeltaMoney(row.expenseDelta)}</td>
                          <td className={`forecast-mono ${metricDeltaClass(row.provisionDelta, true)}`}>{formatDeltaMoney(row.provisionDelta)}</td>
                          <td className={`forecast-mono ${metricDeltaClass(row.netIncomeDelta)}`}>{formatDeltaMoney(row.netIncomeDelta)}</td>
                          <td className={`forecast-mono ${metricDeltaClass(row.loansDelta)}`}>{formatDeltaMoney(row.loansDelta)}</td>
                          <td className={`forecast-mono ${metricDeltaClass(row.depositsDelta)}`}>{formatDeltaMoney(row.depositsDelta)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="macro-waterfall" aria-label="Cumulative NI delta waterfall">
                  <h3>Cumulative NI delta</h3>
                  {waterfallRows.length === 0 ? (
                    <p className="forecast-submit-note">No waterfall until server comparison returns rows.</p>
                  ) : waterfallRows.map((row) => {
                    const width = Math.max(4, (Math.abs(row.delta) / waterfallMax) * 100);
                    return (
                      <div className="macro-waterfall-row" key={row.year}>
                        <span>FY{row.year}</span>
                        <div className="macro-waterfall-track">
                          <span
                            className={`macro-waterfall-bar ${row.delta >= 0 ? 'pos' : 'neg'}`}
                            style={{ width: `${width}%` }}
                          />
                        </div>
                        <strong>{formatDeltaMoney(row.end)}</strong>
                      </div>
                    );
                  })}
                </div>

                <div className="macro-variable-diff-panel">
                  <h3>Variable diff</h3>
                  <div className="macro-impact-diff">
                    {compareDiffRows.slice(0, 40).map((row) => (
                      <span key={`compare-impact-${row.key}`} className="forecast-health-chip neutral">
                        {row.variable} FY{row.year}: {row.delta > 0 ? '+' : ''}{formatNumber(row.delta, row.digits)}
                      </span>
                    ))}
                    {compareDiffRows.length === 0 && <span className="forecast-health-chip neutral">No variable delta</span>}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <p className="forecast-submit-note">
                Variable-only diff. Forecast impact totals are not computed in the browser.
              </p>
              <div className="macro-impact-diff">
                {diffRows.map((row) => (
                  <span key={`impact-${row.key}`} className="forecast-health-chip neutral">
                    {row.variable} FY{row.year}: {row.delta > 0 ? '+' : ''}{formatNumber(row.delta, row.digits)}
                  </span>
                ))}
                {diffRows.length === 0 && <span className="forecast-health-chip neutral">No variable diff available</span>}
              </div>
            </>
          )}
        </div>
      </section>

      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <div>
            <span className="forecast-section-code">MX_04 Event log</span>
            <h2>Scenario status history</h2>
          </div>
          <History size={18} />
        </div>
        <div className="macro-event-log">
          {eventRows.length === 0 ? (
            <p className="forecast-submit-note">No scenario events available.</p>
          ) : eventRows.map((event) => (
            <div className="macro-event-row" key={event.id}>
              <span className="forecast-health-chip neutral">{event.event}</span>
              <strong>{event.actor}</strong>
              <span>{formatDateTime(event.at)}</span>
              <p>{event.note}</p>
            </div>
          ))}
        </div>
      </section>

      {noteDialog && (
        <div className="macro-note-dialog-backdrop" role="presentation">
          <div className="macro-note-dialog" role="dialog" aria-modal="true" aria-labelledby="macro-note-dialog-title">
            <div className="forecast-panel-head">
              <div>
                <span className="forecast-section-code">
                  {noteDialog.action === 'publish' ? 'Publish note' : 'Archive note'}
                </span>
                <h2 id="macro-note-dialog-title">
                  {noteDialog.action === 'publish' ? 'Publish scenario' : 'Archive scenario'}
                </h2>
              </div>
            </div>
            <label className="forecast-submit-narrative">
              <span>Required {noteDialog.action} note</span>
              <textarea
                aria-label={`Required ${noteDialog.action} note`}
                value={noteDraft}
                onChange={(event) => setNoteDraft(event.target.value)}
                autoFocus
              />
            </label>
            {noteDialog.action === 'publish' && (
              <label className="macro-rebuild-anchors-toggle" style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={rebuildAnchorsOnPublish}
                  onChange={(event) => setRebuildAnchorsOnPublish(event.target.checked)}
                />
                <span>
                  <strong>Rebuild anchors now</strong>
                  <small className="forecast-submit-note" style={{ display: 'block' }}>
                    Re-anchor every unit's driver view to this published version on publish
                    (supersedes active anchors). Leave unchecked to let inputters re-anchor
                    from the workspace.
                  </small>
                </span>
              </label>
            )}
            {noteError && <p className="forecast-submit-note error-list">{noteError}</p>}
            <div className="forecast-submit-actions">
              <button type="button" className="forecast-primary-button" onClick={handleActionWithNote}>
                Confirm {noteDialog.action}
              </button>
              <button type="button" className="forecast-secondary-button" onClick={closeNoteDialog}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ForecastMacroScenarios;
